import React, { useMemo, useState } from "react";
import { api, formatApiError, API } from "../lib/api";
import { X, Upload, Sparkles, AlertTriangle, Check, FileText, Image as ImageIcon, Loader2 } from "lucide-react";
import Combobox from "./Combobox.jsx";
import { CURRENCIES, PAYMENT_MODES, PARTY_TYPES } from "../lib/constants";
import { toast } from "sonner";

/**
 * ScanPanel — full-screen modal.
 * Steps: upload -> scan -> review -> apply
 *
 * Props:
 *   onClose(): close without applying
 *   onApply({ formPatch, attachments, aiExtraction }): merge into Entry form and close
 *   masters: { companies, accounts, categories, parties, pis }
 *   addMaster(kind, name, extra): async — creates a new master
 */
export default function ScanPanel({ onClose, onApply, masters, addMaster }) {
  const [files, setFiles] = useState([]); // uploaded file records from /api/uploads
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [reviewed, setReviewed] = useState({});
  const [pickedMatches, setPickedMatches] = useState({});
  const [uploading, setUploading] = useState(false);

  const handleFileInput = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      list.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFiles((f) => [...f, ...(data.files || [])]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeFile = (id) => setFiles((f) => f.filter((x) => x.id !== id));

  const runScan = async () => {
    if (files.length === 0) { toast.error("Please upload at least one file"); return; }
    setScanning(true);
    try {
      const { data } = await api.post("/scan", { file_ids: files.map((f) => f.id) });
      setScanResult(data);
      // seed reviewed with extracted values
      const ext = data.extracted || {};
      setReviewed({
        transaction_date: ext.transaction_date || "",
        amount: ext.amount ?? "",
        currency: ext.currency || "",
        sender_name: ext.sender_name || "",
        receiver_name: ext.receiver_name || "",
        pi_number: ext.pi_number || "",
        account: ext.account || "",
        payment_mode: ext.payment_mode || "",
        reference: ext.reference || "",
        remarks: ext.remarks || "",
      });
      // Auto-pick exact matches
      const pm = data.matches || {};
      const pick = {};
      if (pm.party?.matched?.name) pick.party = pm.party.matched.name;
      if (pm.pi?.matched?.name) pick.pi = pm.pi.matched.name;
      if (pm.account?.matched?.name) pick.account = pm.account.matched.name;
      setPickedMatches(pick);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setScanning(false);
    }
  };

  const set = (k, v) => setReviewed((r) => ({ ...r, [k]: v }));
  const setPick = (k, v) => setPickedMatches((p) => ({ ...p, [k]: v }));

  const apply = () => {
    if (!scanResult) return;
    const partyName = pickedMatches.party || reviewed.sender_name || reviewed.receiver_name || "";
    const partyMasterMatched = scanResult.matches?.party?.matched;
    // Determine party_type from matched master or leave blank
    const partyType = partyMasterMatched?.subtype || "";
    // Company from exact receiver_company match (only auto-pick on exact)
    const companyMatched = scanResult.matches?.receiver_company;
    const company = companyMatched?.matched?.name || "";

    const patch = {
      transaction_date: reviewed.transaction_date || undefined,
      amount: reviewed.amount !== "" ? String(reviewed.amount) : undefined,
      currency: reviewed.currency || undefined,
      payment_mode: reviewed.payment_mode || undefined,
      account: pickedMatches.account || undefined,
      pi_number: pickedMatches.pi || undefined,
      party_type: partyType || undefined,
      party_name: partyName || undefined,
      company: company || undefined,
      reference: reviewed.reference || undefined,
      purpose: reviewed.remarks || undefined,
    };
    // Attachments
    const attachments = (scanResult.files || []).map((f) => ({
      file_id: f.id,
      storage_path: f.storage_path,
      filename: f.filename,
      mime_type: f.mime_type,
      size: f.size,
    }));
    const aiExtraction = {
      provider: scanResult.provider,
      model: scanResult.model,
      extracted_at: scanResult.extracted_at,
      extracted_values: scanResult.extracted,
      confidences: scanResult.extracted?.confidence || {},
      raw_text: scanResult.extracted?.raw_text || "",
    };
    onApply({ formPatch: patch, attachments, aiExtraction });
  };

  const step = scanResult ? "review" : "upload";
  const conf = scanResult?.extracted?.confidence || {};

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start sm:items-center justify-center overflow-y-auto p-4">
      <div className="w-full max-w-4xl bg-white rounded-2xl border border-slate-200 shadow-xl my-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center">
              <Sparkles size={18} />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">Scan payment proof</h3>
              <p className="text-xs text-slate-500">Upload · AI extract · Review · Apply — nothing is saved automatically</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="scan-close">
            <X size={20} />
          </button>
        </div>

        {step === "upload" && (
          <div className="p-6">
            <label
              htmlFor="scan-files-input"
              className="block border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#0055FF] hover:bg-blue-50/40 transition-colors"
              data-testid="scan-upload-zone"
            >
              <Upload className="mx-auto text-slate-400 mb-3" size={28} />
              <div className="font-semibold text-slate-800">Drop or click to upload</div>
              <div className="text-xs text-slate-500 mt-1">JPG · PNG · WebP · PDF · up to 15 MB each · multiple files ok</div>
              <input
                id="scan-files-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                multiple
                className="hidden"
                onChange={handleFileInput}
                data-testid="scan-file-input"
              />
            </label>

            {uploading && (
              <div className="mt-4 text-sm text-slate-500 flex items-center gap-2">
                <Loader2 className="animate-spin" size={14} /> Uploading…
              </div>
            )}

            {files.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="scan-file-list">
                {files.map((f) => (
                  <div key={f.id} className="flex items-center gap-3 border border-slate-200 rounded-lg px-3 py-2">
                    {f.mime_type?.startsWith("image/") ? (
                      <ImageIcon size={16} className="text-slate-500 shrink-0" />
                    ) : (
                      <FileText size={16} className="text-slate-500 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{f.original_filename}</div>
                      <div className="text-xs text-slate-500">{(f.size / 1024).toFixed(1)} KB · {f.mime_type}</div>
                    </div>
                    <button onClick={() => removeFile(f.id)} className="text-slate-400 hover:text-red-600" data-testid={`scan-remove-${f.id}`}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={onClose} className="h-11 px-4 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-semibold" data-testid="scan-cancel">
                Cancel
              </button>
              <button
                onClick={runScan}
                disabled={files.length === 0 || scanning || uploading}
                data-testid="scan-run"
                className="h-11 px-5 rounded-xl bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
              >
                {scanning ? <><Loader2 className="animate-spin" size={16} /> Scanning…</> : <><Sparkles size={16} /> Extract with AI</>}
              </button>
            </div>
          </div>
        )}

        {step === "review" && scanResult && (
          <div className="p-6 space-y-6">
            {scanResult.duplicates?.length > 0 && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex gap-3" data-testid="scan-duplicate-warning">
                <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={20} />
                <div>
                  <div className="font-semibold text-amber-900 text-sm">Possible duplicate transaction</div>
                  <div className="text-xs text-amber-800 mt-1">
                    We found {scanResult.duplicates.length} similar entr{scanResult.duplicates.length === 1 ? "y" : "ies"} in your register. Please check before saving.
                  </div>
                  <ul className="mt-2 space-y-1 text-xs text-amber-900">
                    {scanResult.duplicates.slice(0, 3).map((d) => (
                      <li key={d.id} className="font-mono">
                        {d.transaction_date?.slice(0,10)} · {d.currency} {d.amount} · {d.party_name || "—"} · ref: {d.reference || "—"}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div>
              <div className="section-label mb-3">Extracted fields — review & correct</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ReviewField label="Transaction Date" confidence={conf.transaction_date} testid="rv-date">
                  <input type="date" value={reviewed.transaction_date} onChange={(e) => set("transaction_date", e.target.value)}
                    data-testid="rv-date-input"
                    className="w-full h-11 px-3 border border-slate-300 rounded-lg text-sm outline-none" />
                </ReviewField>
                <ReviewField label="Amount" confidence={conf.amount} testid="rv-amount">
                  <input type="number" step="0.01" value={reviewed.amount} onChange={(e) => set("amount", e.target.value)}
                    data-testid="rv-amount-input"
                    className="w-full h-11 px-3 border border-slate-300 rounded-lg text-sm outline-none text-right tabular" />
                </ReviewField>
                <ReviewField label="Currency" confidence={conf.currency} testid="rv-currency">
                  <select value={reviewed.currency} onChange={(e) => set("currency", e.target.value)}
                    data-testid="rv-currency-input"
                    className="w-full h-11 px-3 border border-slate-300 rounded-lg text-sm outline-none bg-white">
                    <option value="">—</option>
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </ReviewField>
                <ReviewField label="Payment Mode" confidence={conf.payment_mode} testid="rv-mode">
                  <select value={reviewed.payment_mode} onChange={(e) => set("payment_mode", e.target.value)}
                    data-testid="rv-mode-input"
                    className="w-full h-11 px-3 border border-slate-300 rounded-lg text-sm outline-none bg-white">
                    <option value="">—</option>
                    {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </ReviewField>
                <ReviewField label="Sender / Payer" confidence={conf.sender_name} testid="rv-sender">
                  <input value={reviewed.sender_name} onChange={(e) => set("sender_name", e.target.value)}
                    data-testid="rv-sender-input"
                    className="w-full h-11 px-3 border border-slate-300 rounded-lg text-sm outline-none" />
                </ReviewField>
                <ReviewField label="Receiver / Beneficiary" confidence={conf.receiver_name} testid="rv-receiver">
                  <input value={reviewed.receiver_name} onChange={(e) => set("receiver_name", e.target.value)}
                    data-testid="rv-receiver-input"
                    className="w-full h-11 px-3 border border-slate-300 rounded-lg text-sm outline-none" />
                </ReviewField>
                <ReviewField label="Reference Number" confidence={conf.reference} testid="rv-ref">
                  <input value={reviewed.reference} onChange={(e) => set("reference", e.target.value)}
                    data-testid="rv-ref-input"
                    className="w-full h-11 px-3 border border-slate-300 rounded-lg text-sm outline-none" />
                </ReviewField>
                <ReviewField label="Remarks" confidence={conf.remarks} testid="rv-remarks">
                  <input value={reviewed.remarks} onChange={(e) => set("remarks", e.target.value)}
                    data-testid="rv-remarks-input"
                    className="w-full h-11 px-3 border border-slate-300 rounded-lg text-sm outline-none" />
                </ReviewField>
              </div>
            </div>

            {/* Master matching section */}
            <div>
              <div className="section-label mb-3">Match to existing records</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <MatchField
                  label="Party"
                  extractedValue={reviewed.sender_name || reviewed.receiver_name}
                  confidence={conf.sender_name || conf.receiver_name}
                  matches={scanResult.matches?.party}
                  value={pickedMatches.party}
                  onChange={(v) => setPick("party", v)}
                  options={(masters.parties || []).map((p) => ({ value: p.name, label: `${p.name} · ${p.subtype || "?"}` }))}
                  testid="match-party"
                />
                <MatchField
                  label="PI Number"
                  extractedValue={reviewed.pi_number}
                  confidence={conf.pi_number}
                  matches={scanResult.matches?.pi}
                  value={pickedMatches.pi}
                  onChange={(v) => setPick("pi", v)}
                  options={(masters.pis || []).map((p) => ({ value: p.name, label: p.name }))}
                  testid="match-pi"
                />
                <MatchField
                  label="Account"
                  extractedValue={reviewed.account}
                  confidence={conf.account}
                  matches={scanResult.matches?.account}
                  value={pickedMatches.account}
                  onChange={(v) => setPick("account", v)}
                  options={(masters.accounts || []).map((a) => ({ value: a.name, label: `${a.name}${a.currency ? " (" + a.currency + ")" : ""}` }))}
                  testid="match-account"
                />
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Not creating new masters automatically. If nothing matches, leave blank and add it after applying.
              </p>
            </div>

            {/* Raw text collapsible */}
            {scanResult.extracted?.raw_text && (
              <details className="border border-slate-200 rounded-xl p-3">
                <summary className="text-sm font-semibold text-slate-700 cursor-pointer">Full extracted text</summary>
                <pre className="mt-2 text-xs text-slate-600 whitespace-pre-wrap max-h-40 overflow-y-auto">{scanResult.extracted.raw_text}</pre>
              </details>
            )}

            {/* Actions */}
            <div className="flex justify-between items-center pt-2 border-t border-slate-100">
              <div className="text-xs text-slate-500">
                Model: <span className="font-mono">{scanResult.provider}/{scanResult.model}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setScanResult(null)} className="h-11 px-4 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-semibold" data-testid="scan-back">
                  ← Back
                </button>
                <button onClick={onClose} className="h-11 px-4 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-semibold" data-testid="scan-discard">
                  Discard
                </button>
                <button onClick={apply} data-testid="scan-apply"
                  className="h-11 px-5 rounded-xl bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-semibold inline-flex items-center gap-2">
                  <Check size={16} /> Apply to form
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function confBadge(c) {
  if (c === undefined || c === null) return { label: "—", cls: "bg-slate-100 text-slate-500" };
  if (c >= 0.8) return { label: `${Math.round(c * 100)}% high`, cls: "bg-emerald-50 text-emerald-700 border border-emerald-200" };
  if (c >= 0.5) return { label: `${Math.round(c * 100)}% med`, cls: "bg-amber-50 text-amber-700 border border-amber-200" };
  if (c > 0)    return { label: `${Math.round(c * 100)}% low`, cls: "bg-red-50 text-red-700 border border-red-200" };
  return { label: "not identified", cls: "bg-slate-100 text-slate-500 border border-slate-200" };
}

function ReviewField({ label, confidence, children, testid }) {
  const b = confBadge(confidence);
  const lowConf = (confidence ?? 0) < 0.5;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}</label>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${b.cls}`}>{b.label}</span>
      </div>
      <div className={lowConf ? "ring-2 ring-amber-200 rounded-lg" : ""}>
        {children}
      </div>
    </div>
  );
}

function MatchField({ label, extractedValue, confidence, matches, value, onChange, options, testid }) {
  const matched = matches?.matched;
  const candidates = matches?.candidates || [];
  return (
    <div data-testid={testid}>
      <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">{label}</div>
      <div className="text-xs text-slate-500 mb-2">
        Extracted: <span className="font-medium text-slate-800">{extractedValue || "—"}</span>
      </div>
      {matched && !value && (
        <div className="text-[11px] text-emerald-700 mb-1">✓ Exact match auto-picked</div>
      )}
      {candidates.length > 0 && !matched && (
        <div className="text-[11px] text-amber-700 mb-1">Suggestions — please pick</div>
      )}
      <Combobox
        value={value || ""}
        onChange={onChange}
        options={options}
        placeholder="Select or leave blank…"
        testid={`${testid}-picker`}
      />
      {candidates.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {candidates.slice(0, 3).map((c) => (
            <button
              key={c.id}
              onClick={() => onChange(c.name)}
              className="text-[11px] px-2 py-0.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700"
              data-testid={`${testid}-cand-${c.name}`}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
